import { Layers3 } from 'lucide-react'
import { LEG_ROLE_LABEL, LEG_ROLE_MEANING } from '@apna/shared'
import { cn } from '@/lib/utils'
import { dayLabel, hhmmss, ivPct, money } from '../utils/format'
import type { JournalLeg } from '../types'

/**
 * One execution time, with its day only when the page spans more than one.
 *
 * SECONDS, because two legs of a basket can print in the same minute and
 * the order they printed in is the thing a trader is looking for — 5.9
 * sends protective buys before premium-collecting sells, and this column is
 * where that is checked.
 */
function TradedAt({
  at,
  legs,
  reviewed = false,
}: {
  at: string | null
  legs: readonly JournalLeg[]
  reviewed?: boolean
}) {
  if (at === null) {
    return <td className="num text-muted-foreground">—</td>
  }
  const days = new Set(
    legs.flatMap((l) => [l.entryAt, l.exitAt].filter((v): v is string => v !== null)).map(dayLabel),
  )
  return (
    <td className="num whitespace-nowrap text-[10.5px]">
      {days.size > 1 && <span className="mr-1 text-[9px] text-muted-foreground">{dayLabel(at)}</span>}
      {hhmmss(at)}
      {/* A reviewed close is a time a human supplied, like the price beside
          it. The ADJ badge on the price says so; this stays quiet and
          borrows it rather than repeating the word. */}
      {reviewed && <span className="ml-0.5 text-[8px] text-warning" title="From a review, not a broker fill">*</span>}
    </td>
  )
}

export function JournalLegs({ legs }: { legs: readonly JournalLeg[] }) {
  return (
    <section className="jn-panel" aria-label="Position breakdown">
      <header className="jn-panel-heading">
        <div className="flex items-center gap-2"><Layers3 size={14} className="text-primary" /><h3>Position breakdown</h3><span className="jn-badge">{legs.length} leg{legs.length === 1 ? '' : 's'}</span></div>
        <span className="jn-caption">Recorded fills</span>
      </header>
      <div className="jn-legs-scroll">
        <table className="jn-legs-table">
          <thead><tr>
            <th className="w-[28%] text-left">LEG</th><th>QTY</th><th>ENTRY</th><th>IN AT</th><th>EXIT</th><th>OUT AT</th><th>IV IN</th><th>IV OUT</th><th>P&amp;L</th>
          </tr></thead>
          <tbody>{legs.map((leg) => {
            const tail = `${Math.round(leg.strike)} ${leg.optionType}`
            const prefix = leg.symbol.endsWith(tail) ? leg.symbol.slice(0, -tail.length).trim() : leg.symbol
            return (
              <tr key={leg.tradeLegId} data-testid={`journal-leg-${leg.tradeLegId}`}>
                <td><span className="flex min-w-0 items-center gap-2">
                  <span className={cn('jn-side', leg.side === 'BUY' ? 'is-buy' : 'is-sell')}>{leg.side === 'BUY' ? 'B' : 'S'}</span>
                  <span className="num min-w-0 truncate" title={leg.symbol}><span className="text-[10px] text-muted-foreground">{prefix} </span><span className={cn('font-semibold', leg.optionType === 'CE' ? 'text-primary' : 'text-warning')}>{tail}</span></span>
                  {leg.role && (
                    <span
                      className={cn('jn-leg-role', `is-${leg.role.toLowerCase()}`)}
                      title={LEG_ROLE_MEANING[leg.role]}
                    >
                      {LEG_ROLE_LABEL[leg.role]}
                    </span>
                  )}
                </span></td>
                <td className="num">{leg.qty.toLocaleString('en-IN')}</td>
                <td className="num">{leg.entryPrice === null ? '—' : leg.entryPrice.toFixed(2)}</td>
                {/*
                  WHEN IT ACTUALLY TRADED — 9.55. The broker's execution
                  time, to the second, from `Fill.at`. The day is shown
                  only when it differs from the trade's other end, so an
                  intraday basket stays six short columns and an overnight
                  one still says which day each leg moved.
                */}
                <TradedAt at={leg.entryAt} legs={legs} />
                <td className="num">{leg.exitPrice === null ? '—' : leg.exitPrice.toFixed(2)}{leg.exitPriceSource === 'ADJUSTED' && <span className="ml-1 text-[8px] font-bold text-warning" title="Typed during a review, not a broker fill">ADJ</span>}</td>
                <TradedAt at={leg.exitAt} legs={legs} reviewed={leg.exitPriceSource === 'ADJUSTED'} />
                <td className="num">{ivPct(leg.entryIv)}</td><td className="num">{ivPct(leg.exitIv)}</td>
                <td className={cn('num font-semibold', leg.realisedPnl >= 0 ? 'text-success' : 'text-destructive')}>{money(leg.realisedPnl)}</td>
              </tr>
            )
          })}</tbody>
        </table>
      </div>
    </section>
  )
}
